import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import type { TenantContextType, TenantMembership, StoredSession, SwitchTenantResponse } from '../types';
import { sanitizeRedirectTarget } from '../utils/safeRedirect';
import { fetchCurrentSession } from '../utils/sessionFetch';

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider = ({ children }: TenantProviderProps) => {
  const [currentTenant, setCurrentTenant] = useState<TenantMembership | null>(null);
  const [availableTenants, setAvailableTenants] = useState<TenantMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize tenant context from session API
  useEffect(() => {
    const loadSession = async () => {
      try {
        const response = await fetchCurrentSession();

        if (!response.ok) {
          if (response.status === 401) {
            // Session expired/invalid — clear state and redirect to login
            try {
              localStorage.removeItem('session');
              localStorage.removeItem('elli-current-tenant-id');
              localStorage.removeItem('elli-user-profile');
              localStorage.removeItem('elli-sidebar-collapsed');
            } catch { /* test env */ }
            window.location.href = '/';
            return;
          }
          setLoading(false);
          return;
        }

        const session: StoredSession = await response.json();
        setAvailableTenants(session.memberships || []);

        // Use primary tenant from session (server is source of truth)
        const primaryTenant = session.memberships.find(m => m.tenantId === session.primaryTenantId);
        const selectedTenant = primaryTenant || session.memberships[0] || null;
        setCurrentTenant(selectedTenant);
      } catch (err) {
        console.error('Failed to initialize tenant context:', err);
        setError('Failed to load tenant information');
      } finally {
        setLoading(false);
      }
    };

    loadSession();
  }, []);

  // Update document title with tenant name
  useEffect(() => {
    if (currentTenant?.tenantName) {
      document.title = `Elli Workspace - ${currentTenant.tenantName}`;
    } else {
      document.title = 'Elli Workspace';
    }
  }, [currentTenant]);

  const switchTenant = async (tenantId: string): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      // Get current session to verify tenant membership
      const sessionResponse = await fetch('/api/session/current', {
        credentials: 'include',
      });

      if (!sessionResponse.ok) {
        throw new Error('No session found');
      }

      const session: StoredSession = await sessionResponse.json();

      // Verify tenant exists in memberships
      const newTenant = session.memberships.find(m => m.tenantId === tenantId);
      if (!newTenant) {
        throw new Error('You do not have access to this tenant');
      }

      // Call BFF endpoint to switch tenant (updates httpOnly cookie)
      const response = await fetch('/api/session/switch-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ tenant_id: tenantId }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Failed to switch tenant' }));
        throw new Error(errorData.detail || 'Failed to switch tenant');
      }

      const newSession: SwitchTenantResponse = await response.json();

      // Update context state
      setCurrentTenant(newTenant);

      // Reload page to refresh all components with new tenant context
      // This ensures httpOnly cookie is fresh and all API calls use correct tenant.
      // sanitizeRedirectTarget enforces an http(s) scheme allowlist (defense-in-depth
      // against javascript:/data: schemes) — see utils/safeRedirect.ts.
      window.location.href = sanitizeRedirectTarget(newSession.redirectPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred while switching tenant';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCurrentTenant = useCallback((updates: Partial<TenantMembership>) => {
    setCurrentTenant(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  const value: TenantContextType = {
    currentTenant,
    availableTenants,
    switchTenant,
    updateCurrentTenant,
    loading,
    error,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
